
import React, { Component } from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import { Divider, message } from 'antd';
import baseActions from '../actions';
import {
  DASHBOARDS,
  VIEW_LAYOUTS,
  VISUALISATIONS,
  VISUALISATION_DATA,
  GLOBAL_FILTERS,
  PERSIST_PARAMS,
} from '../plannerConfig';
import PageWithTopbar from '../components/PageWithTopbar';
import Dashboard from '../components/Dashboard';
import WeekDayPicker from '../components/WeekDayPicker';
import {
  generateRouteParams, updateLink, getSetSequence, getFilters,
} from '../helpers';
import { GeneralFilter, mapGlobalFilterKeysToObj } from '../components/Filters';
import { ShowAppliedFilter } from '../components/Filters/ShowAppliedFilter';
import { getParsedVisualisationsAsObject } from '../selectors/dashboard';
import { DEPRECATED_DASHBOARD_FILTERS } from '../constants/text.constants';

const mapStateToProps = state => ({
  [DASHBOARDS]: state[DASHBOARDS].data,
  [VIEW_LAYOUTS]: state[VIEW_LAYOUTS].data,
  [VISUALISATIONS]: getParsedVisualisationsAsObject(state),
  [VISUALISATION_DATA]: state[VISUALISATION_DATA],
  [GLOBAL_FILTERS]: state[GLOBAL_FILTERS],
  [PERSIST_PARAMS]: state[PERSIST_PARAMS].data,
});

const mapDispatchToProps = {
  get: baseActions.get,
  put: baseActions.put,
  post: baseActions.post,
  apiDelete: baseActions.delete,
  set: baseActions.set,
};

export class DashboardWrapperComponent extends Component {
  state = {
    initialApplyFilterCalled: false, // Prevent dashboard mount before applyStoredFilter method is called.
    globalFilterApplied: [],
    kpiFilterApplied: [],
  };

  componentDidMount() {
    getFilters(this.props, null, null, () => {
      getSetSequence(this.props, [
        [DASHBOARDS, VIEW_LAYOUTS, VISUALISATIONS],
      ]);
    });
  }

  componentDidUpdate(prevProps) {
    const { match } = this.props;
    if (prevProps.match.url === match.url) return false;
    getSetSequence(this.props, []);
    return true;
  }

  // Save the filter to the applied state to actually apply filter across dashboard elements.
  handleClickToApplyFilter = (globalFilterHook, kpiFilterHook) => {
    this.setState({
      globalFilterApplied: globalFilterHook,
      kpiFilterApplied: kpiFilterHook,
    });
  }

  handleClickToToggleFilterView = () => {
    const { set, persistParams } = this.props;
    set([PERSIST_PARAMS], { hideFilterView: !persistParams.hideFilterView });
  }

  // A collection of actions that can be applied to chart-interactions.
  visualisationActions = props => ({
    selectWeekDay: (e) => {
      const selected = moment.weekdays().find(str => str === e.select.name);

      if (!selected) return false;
      return updateLink(
        {
          range: 1,
          offset: moment().day(selected).format('E'),
        },
        props,
        true,
      );
    },
  })

  applyStoredFilter = (filterArray = []) => {
    const globalFilterHook = [];
    const kpiFilterHook = [];
    const {
      initialApplyFilterCalled,
      globalFilters: { data: { globalFilter = [], kpiFilter = [] } },
    } = this.props;
    let throwWarning = false;

    if (Array.isArray(filterArray)) { // Some older dashboards may have null, which skips the default [].
      filterArray.forEach((item) => {
        const key = item.split('-|');

        // Perform a check to see if filters are applicable, if is using old delimiters, give user a
        // warning to update their filters for old tabs.
        //
        // The magic number 2 is because Antd uses "<root><delimiter><parent><delimiter><child>" this
        // string format for display. For processing, this string needs to be split in array.
        // And the reason why is 2 is because if there a tag with parent without defining a child,
        // Antd will treat this as select all children.
        const foundOldDelimiters = key.length < 2; // If delimiters no longer applies.
        const cannotFindFilterCategory = ( // If the categories no longer applies.
          !globalFilter.list[key[1]]
          && !kpiFilter.list[key[1]]
        );

        if (foundOldDelimiters || cannotFindFilterCategory) {
          // This warning message is not applied at the leaf level due to complex logic.
          // TODO: Make this happen.
          throwWarning = true;
        }

        if (globalFilter.list && globalFilter.list[key[1]]) globalFilterHook.push(item);
        if (kpiFilter.list && kpiFilter.list[key[1]]) kpiFilterHook.push(item);
      });
    }
    // The detection of leaf level is done in ShowAppliedFilter.js due to complex logic.
    // But the leaf level is not throwing message, instead, it shows it as problematic tag
    // which can be easily replaceble.
    if (throwWarning) message.warning(DEPRECATED_DASHBOARD_FILTERS, 4);

    this.handleClickToApplyFilter(globalFilterHook, kpiFilterHook);

    // Once stored filter applied, dashboard now can be mounted.
    if (!initialApplyFilterCalled) this.setInitialApplyFilterCalled(true);
  }
  

  setInitialApplyFilterCalled = boolean => this.setState({ initialApplyFilterCalled: boolean });

  render() {
    const {
      dashboards, apiDelete, put, post, set, globalFilters, viewLayouts, persistParams,
    } = this.props;
    const { globalFilterApplied, kpiFilterApplied, initialApplyFilterCalled } = this.state;
    // TODO: The visualisation needs generateRouteParams this for unknown reason,
    // look forward to remove it.
    const routeParams = generateRouteParams(this.props);
    const context = parseInt(routeParams.range, 10) === 7 ? 'week' : 'day';
    const globalFilterReady = (globalFilters.loadingQueue.length === 0);

    const weekUseStartWeekDate = (context === 'week')
      ? { startDate: routeParams.startOfWeekDate, endDate: routeParams.endOfWeekDate }
      : {};

    return dashboards.length > 0 && (
      <PageWithTopbar
        leftComponent={globalFilterReady && (
          <>
            <GeneralFilter
              filterObj={globalFilters.data}
              onClickToApplyFilter={this.handleClickToApplyFilter}
              globalFilterApplied={globalFilterApplied}
              kpiFilterApplied={kpiFilterApplied}
              onClickToToggleFilterView={this.handleClickToToggleFilterView}
              hideView={persistParams.hideFilterView}
            />
            <Divider type="vertical" />
            <ShowAppliedFilter
              globalFilterApplied={globalFilterApplied}
              kpiFilterApplied={kpiFilterApplied}
              filterObj={globalFilters.data}
              hideView={persistParams.hideFilterView}
            />
          </>
        )}
        rightComponent={(
          <WeekDayPicker
            startDate={persistParams.startOfWeekDate}
            selection={persistParams.offset}
            onChange={e => updateLink(
              {
                startOfWeekDate: persistParams.startOfWeekDate,
                offset: e.target.value,
                range: (e.target.value === 0) ? 7 : 1,
              },
              this.props,
            )}
          />
        )}
      >
        <Dashboard
          viewLayouts={viewLayouts}
          routeParams={routeParams}
          context={context}
          setInitialApplyFilterCalled={this.setInitialApplyFilterCalled}
          initialApplyFilterCalled={initialApplyFilterCalled}
          applyStoredFilter={this.applyStoredFilter}
          globalFilterApplied={[...globalFilterApplied, ...kpiFilterApplied]}
          globalFilterDef={globalFilters ? {
            ...globalFilters.data.globalFilter.list,
            ...globalFilters.data.kpiFilter.list,
          } : {}}
          api={{
            getVisualisation: (dataType, unit = 'None', applyGlobalFilter, callback) => post(
              [VISUALISATION_DATA],
              {
                routeParams: {
                  ...routeParams,
                  ...weekUseStartWeekDate, // if context is week, start/end date should use the week range.
                  dataType,
                  unit,
                },
                body: (
                  applyGlobalFilter
                  && (globalFilterApplied.length + kpiFilterApplied.length) > 0
                ) ? mapGlobalFilterKeysToObj([...globalFilterApplied, ...kpiFilterApplied])
                  : {},
                callback,
              },
            ),
            resetDashboard: newDashboards => set(
              [DASHBOARDS],
              newDashboards,
            ),
            addDashboard: (pk, body, callback) => post(
              [DASHBOARDS],
              {
                routeParams: {},
                body,
                callback,
              },
            ),
            updateDashboard: (dashboardId, body, callback) => put(
              [DASHBOARDS],
              {
                routeParams: {
                  dashboardId,
                },
                body,
                callback,
              },
            ),
            deleteDashboard: (dashboardId, callback) => apiDelete(
              [DASHBOARDS],
              {
                routeParams: {
                  dashboardId,
                },
                deletedRecordId: dashboardId,
                callback,
              },
            ),
          }}
          visualisationActions={this.visualisationActions(this.props)}
          {...this.props}
        />
      </PageWithTopbar>
    );
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(DashboardWrapperComponent);
